import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useGetPost, useGetSpace } from '@onesource/services';
import { useNavigate, useParams } from '@tanstack/react-router';

import { PostCard } from './post-card';
import { SpacePostDetailSkeleton } from './space-post-detail-skeleton';

/**
 * Space Post Detail Page
 * Shows a single post with full content, reactions, and comments.
 * Renders through PostCard (variant="detail") so the feed and detail stay in sync.
 */
export const SpacePostDetailPage = () => {
  const navigate = useNavigate();
  const { id: spaceId, postId } = useParams({ from: '/_protected/community-hub/spaces/$id/posts/$postId' });

  const { data: post, isLoading: isLoadingPost, error: postError } = useGetPost(postId);
  const { data: space } = useGetSpace(spaceId);

  const handleBackToSpace = () => {
    navigate({ to: '/community-hub/spaces/$id/community', params: { id: spaceId } });
  };

  if (isLoadingPost) {
    return <SpacePostDetailSkeleton />;
  }

  if (postError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load post: {postError.message}</Alert>
      </Box>
    );
  }

  if (!post) {
    return (
      <Box p={3}>
        <Alert severity="error">Post not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          cursor: 'pointer',
        }}
        onClick={handleBackToSpace}
      >
        <ArrowBackIcon sx={{ fontSize: 20, color: '#333' }} />
        <Typography
          sx={{
            fontFamily: 'Figtree, sans-serif',
            fontWeight: 500,
            fontSize: 14,
            color: '#333',
          }}
        >
          Back to {space?.name || 'Community'}
        </Typography>
      </Box>

      <PostCard post={post} spaceId={spaceId} variant="detail" onAfterDelete={handleBackToSpace} />
    </Box>
  );
};
